/* eslint-disable */
import React, { useState, useEffect, useCallback, useRef } from 'react';
import {
  TextField,
  Button,
  Stack,
  Typography,
  FormControlLabel,
  Checkbox,
  Alert,
  Card,
  CardContent,
  Stepper,
  Step,
  StepLabel,
  Box,
  Chip,
  IconButton,
  Container,
  Fade,
  Divider,
  Autocomplete,
  CircularProgress,
  LinearProgress,
  AppBar,
  Toolbar,
  useMediaQuery,
  useTheme,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
} from '@mui/material';
import { alpha } from '@mui/material/styles';
import {
  CloudUpload,
  Delete,
  ArrowForward,
  ArrowBack,
  CheckCircle,
  AttachFile,
  Euro,
  CalendarToday,
  Description,
  Save,
  RestoreFromTrash,
  Person,
  PictureAsPdf,
  OpenInNew,
  Add,
  Edit,
  ReceiptLong,
  Storefront,
  Insights,
  CameraAlt,
  AccountBalance,
  Inventory2,
} from '@mui/icons-material';

import { db, storage } from './firebaseConfig';
import { doc, setDoc, serverTimestamp } from 'firebase/firestore';
import { ref, uploadBytes } from 'firebase/storage';
import { v4 as uuidv4 } from 'uuid';
import { areaOptions, categoryOptions, isValidAmount, isValidEmail, isValidIBAN, validateFile } from './constants';

// ---------------------------------------------------------------------------
// Reusable building blocks
// ---------------------------------------------------------------------------

const SectionCard = ({ children, sx, ...props }) => (
  <Card sx={{ p: { xs: 2.5, sm: 3.5 }, ...sx }} {...props}>
    {children}
  </Card>
);

const SectionHeader = ({ icon, eyebrow, title, subtitle }) => (
  <Box sx={{ mb: 3 }}>
    {eyebrow && (
      <Typography variant="overline" sx={{ color: 'primary.main', fontWeight: 700, letterSpacing: 1.2 }}>
        {eyebrow}
      </Typography>
    )}
    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5, mb: subtitle ? 0.5 : 0 }}>
      {icon && (
        <Box
          sx={{
            width: 40,
            height: 40,
            borderRadius: 2,
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            bgcolor: (t) => alpha(t.palette.primary.main, 0.1),
            color: 'primary.main',
          }}
        >
          {icon}
        </Box>
      )}
      <Typography variant="h5" fontWeight={700} color="text.primary">
        {title}
      </Typography>
    </Box>
    {subtitle && (
      <Typography variant="body2" color="text.secondary">
        {subtitle}
      </Typography>
    )}
  </Box>
);

const FilePreview = ({ file, name, type, height = 220 }) => {
  const url = file?.preview || (file ? URL.createObjectURL(file) : null);
  if (!url || !type) return null;
  if (type.startsWith('image/')) {
    return (
      <Box
        sx={{
          width: '100%',
          height,
          borderRadius: 2,
          overflow: 'hidden',
          backgroundColor: 'background.subtle',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
        }}
      >
        <img
          src={url}
          alt={name || 'Belegvorschau'}
          style={{ maxWidth: '100%', maxHeight: '100%', objectFit: 'contain' }}
        />
      </Box>
    );
  }
  if (type === 'application/pdf') {
    return (
      <Box
        sx={{
          width: '100%',
          height,
          borderRadius: 2,
          overflow: 'hidden',
          border: (t) => `1px solid ${t.palette.divider}`,
          position: 'relative',
        }}
      >
        <iframe src={url} width="100%" height="100%" style={{ border: 'none' }} title="PDF Vorschau" />
      </Box>
    );
  }
  return (
    <Box
      sx={{
        height,
        borderRadius: 2,
        bgcolor: 'background.subtle',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        flexDirection: 'column',
        gap: 1,
      }}
    >
      <AttachFile sx={{ color: 'text.secondary' }} />
      <Typography variant="body2" color="text.secondary">
        {name}
      </Typography>
    </Box>
  );
};

const FieldLabel = ({ children, required }) => (
  <Typography variant="body2" sx={{ fontWeight: 600, color: 'text.primary', mb: 1 }}>
    {children}
    {required && <Box component="span" sx={{ color: 'error.main', ml: 0.5 }}>*</Box>}
  </Typography>
);

const OriginalsBanner = ({ compact }) => (
  <Box
    role="alert"
    sx={{
      display: 'flex',
      alignItems: 'flex-start',
      gap: { xs: 1.5, sm: 2 },
      p: { xs: 2, sm: 2.5 },
      borderRadius: 3,
      bgcolor: (t) => alpha(t.palette.warning.main, 0.12),
      border: (t) => `2px solid ${alpha(t.palette.warning.main, 0.45)}`,
      color: 'warning.dark',
    }}
  >
    <Box
      sx={{
        flexShrink: 0,
        width: { xs: 36, sm: 44 },
        height: { xs: 36, sm: 44 },
        borderRadius: 2,
        bgcolor: 'warning.main',
        color: 'warning.contrastText',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
      }}
    >
      <Inventory2 fontSize={compact ? 'small' : 'medium'} />
    </Box>
    <Box sx={{ flex: 1, minWidth: 0 }}>
      <Typography
        sx={{
          fontWeight: 800,
          fontSize: { xs: '1rem', sm: '1.15rem' },
          lineHeight: 1.25,
          color: 'warning.dark',
          mb: 0.5,
        }}
      >
        Wichtig: Originalbelege aufbewahren!
      </Typography>
      <Typography
        sx={{
          fontWeight: 500,
          fontSize: { xs: '0.875rem', sm: '0.95rem' },
          lineHeight: 1.5,
          color: 'warning.dark',
        }}
      >
        Bewahre alle Belege im Original auf, bis das Geld auf deinem Konto eingegangen ist.
        Wir benötigen sie ggf. für Rückfragen oder die Buchhaltung.
      </Typography>
    </Box>
  </Box>
);

// ---------------------------------------------------------------------------
// Main component
// ---------------------------------------------------------------------------

function SubmissionForm() {
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('md'));

  const [activeStep, setActiveStep] = useState(0);

  const mobileSteps = ['Kontaktdaten', 'Belege erfassen', 'Zusammenfassung'];
  const desktopSteps = ['Kontaktdaten', 'Belege hochladen', 'Belege klassifizieren', 'Übersicht'];
  const steps = isMobile ? mobileSteps : desktopSteps;

  const [userData, setUserData] = useState({ name: '', email: '', iban: '', bic: '' });
  const [uploadedFiles, setUploadedFiles] = useState([]);
  const [classifiedReceipts, setClassifiedReceipts] = useState([]);
  const [currentReceiptIndex, setCurrentReceiptIndex] = useState(0);

  const [confirmAmount, setConfirmAmount] = useState(false);
  const [confirmUpload, setConfirmUpload] = useState(false);
  const [success, setSuccess] = useState(false);
  const [bicError, setBicError] = useState(false);
  const [openDialog, setOpenDialog] = useState(false);
  const [savedDataExists, setSavedDataExists] = useState(false);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [fileError, setFileError] = useState('');
  const [isDragOver, setIsDragOver] = useState(false);
  const [editingReceiptId, setEditingReceiptId] = useState(null);
  const [preferredArea, setPreferredArea] = useState('');

  const bicTimeoutRef = useRef(null);

  const savePreferredArea = (area) => {
    if (!area) return;
    setPreferredArea(area);
    try { localStorage.setItem('belegPreferredArea', area); } catch {}
  };

  // ----- LocalStorage -----
  const saveUserDataToLocalStorage = () => {
    const dataToSave = { ...userData, savedAt: new Date().toISOString() };
    localStorage.setItem('belegUserData', JSON.stringify(dataToSave));
    setSavedDataExists(true);
  };

  const loadUserDataFromLocalStorage = () => {
    const savedData = localStorage.getItem('belegUserData');
    if (!savedData) return false;
    try {
      const parsed = JSON.parse(savedData);
      setUserData({
        name: parsed.name || '',
        email: parsed.email || '',
        iban: parsed.iban || '',
        bic: parsed.bic || '',
      });
      setSavedDataExists(true);
      return true;
    } catch {
      return false;
    }
  };

  const clearSavedUserData = () => {
    localStorage.removeItem('belegUserData');
    setSavedDataExists(false);
    setUserData({ name: '', email: '', iban: '', bic: '' });
  };

  const checkForSavedData = () => {
    setSavedDataExists(!!localStorage.getItem('belegUserData'));
  };

  // ----- BIC autoload -----
  const loadBIC = useCallback(async (ibanRaw) => {
    const iban = ibanRaw.replace(/\s+/g, '').toUpperCase();
    if (!isValidIBAN(iban)) {
      if (iban.length >= 22) setBicError(true);
      return;
    }
    try {
      const res = await fetch(`https://openiban.com/validate/${iban}?getBIC=true`);
      const data = await res.json();
      if (data.valid && data.bankData?.bic) {
        setUserData((prev) => ({ ...prev, bic: data.bankData.bic }));
        setBicError(false);
      } else {
        setUserData((prev) => ({ ...prev, bic: '' }));
        setBicError(true);
      }
    } catch {
      setUserData((prev) => ({ ...prev, bic: '' }));
      setBicError(true);
    }
  }, []);

  useEffect(() => {
    if (bicTimeoutRef.current) clearTimeout(bicTimeoutRef.current);
    bicTimeoutRef.current = setTimeout(() => loadBIC(userData.iban), 400);
    return () => {
      if (bicTimeoutRef.current) clearTimeout(bicTimeoutRef.current);
    };
  }, [userData.iban, loadBIC]);

  useEffect(() => {
    checkForSavedData();
    loadUserDataFromLocalStorage();
    try {
      const saved = localStorage.getItem('belegPreferredArea');
      if (saved && areaOptions.includes(saved)) setPreferredArea(saved);
    } catch {}
  }, []);

  useEffect(() => {
    return () => {
      uploadedFiles.forEach((f) => f.preview && URL.revokeObjectURL(f.preview));
    };
  }, []); // eslint-disable-line

  // ----- File handling -----
  const processSingleFile = (file) => {
    const v = validateFile(file);
    if (!v.valid) return setFileError(v.error);
    const newFile = {
      id: uuidv4(),
      file,
      name: file.name,
      size: file.size,
      type: file.type,
      preview: URL.createObjectURL(file),
      area: preferredArea,
    };
    setUploadedFiles([newFile]);
  };

  const processFiles = (files) => {
    const errors = [];
    const valid = files.filter((f) => {
      const v = validateFile(f);
      if (!v.valid) {
        errors.push(`${f.name}: ${v.error}`);
        return false;
      }
      return true;
    });
    if (errors.length) setFileError(errors.join('\n'));
    const mapped = valid.map((f) => ({
      id: uuidv4(),
      file: f,
      name: f.name,
      size: f.size,
      type: f.type,
      preview: URL.createObjectURL(f),
      area: preferredArea,
    }));
    setUploadedFiles((prev) => [...prev, ...mapped]);
  };

  const handleFileUpload = (event) => {
    const files = Array.from(event.target.files);
    setFileError('');
    if (isMobile) {
      if (files.length > 0) processSingleFile(files[0]);
    } else {
      processFiles(files);
    }
    event.target.value = '';
  };

  const handleDragOver = (e) => {
    e.preventDefault();
    e.stopPropagation();
    setIsDragOver(true);
  };
  const handleDragEnter = handleDragOver;
  const handleDragLeave = (e) => {
    e.preventDefault();
    e.stopPropagation();
    setIsDragOver(false);
  };
  const handleDrop = (e) => {
    e.preventDefault();
    e.stopPropagation();
    setIsDragOver(false);
    setFileError('');
    const files = Array.from(e.dataTransfer.files);
    if (!files.length) return;
    if (isMobile) processSingleFile(files[0]);
    else processFiles(files);
  };

  const removeFile = (id) => {
    setUploadedFiles((prev) => {
      const f = prev.find((x) => x.id === id);
      if (f?.preview) URL.revokeObjectURL(f.preview);
      return prev.filter((x) => x.id !== id);
    });
  };

  // ----- Classification -----
  const initializeClassifiedReceipts = () => {
    const receipts = uploadedFiles.map((file) => ({
      id: file.id,
      file: file.file,
      fileName: file.name,
      amount: '',
      purpose: '',
      area: file.area || preferredArea,
      category: '',
      date: new Date().toISOString().split('T')[0],
      paymentType: 'paid',
      classified: false,
    }));
    setClassifiedReceipts(receipts);
  };

  const updateReceiptClassification = (field, value) => {
    setClassifiedReceipts((prev) =>
      prev.map((r, i) => (i === currentReceiptIndex ? { ...r, [field]: value } : r))
    );
  };

  const markReceiptAsClassified = () => {
    setClassifiedReceipts((prev) =>
      prev.map((r, i) => (i === currentReceiptIndex ? { ...r, classified: true } : r))
    );
  };

  const editReceipt = (id) => {
    const r = classifiedReceipts.find((x) => x.id === id);
    if (!r) return;
    setEditingReceiptId(id);
    uploadedFiles.forEach((f) => f.preview && URL.revokeObjectURL(f.preview));
    setUploadedFiles([
      {
        id: r.id,
        file: r.file,
        name: r.fileName,
        size: r.file.size,
        type: r.file.type,
        preview: URL.createObjectURL(r.file),
        amount: r.amount,
        purpose: r.purpose,
        area: r.area,
        category: r.category,
        date: r.date,
        paymentType: r.paymentType,
      },
    ]);
  };

  const deleteReceipt = (id) => setClassifiedReceipts((prev) => prev.filter((r) => r.id !== id));
  const cancelEdit = () => {
    setEditingReceiptId(null);
    setUploadedFiles([]);
  };

  // ----- Navigation -----
  const handleNext = () => {
    if (isMobile) {
      if (activeStep === 0) setActiveStep(1);
      else if (activeStep === 1) setActiveStep(2);
    } else {
      if (activeStep === 1) initializeClassifiedReceipts();
      setActiveStep((p) => p + 1);
    }
  };
  const handleBack = () => setActiveStep((p) => p - 1);
  const handleNextReceipt = () => {
    if (currentReceiptIndex < classifiedReceipts.length - 1) setCurrentReceiptIndex((p) => p + 1);
  };
  const handlePrevReceipt = () => {
    if (currentReceiptIndex > 0) setCurrentReceiptIndex((p) => p - 1);
  };

  const handleUserChange = (e) => {
    const { name, value } = e.target;
    let v = value;
    if (name === 'iban') v = value.toUpperCase();
    setUserData((prev) => ({ ...prev, [name]: v }));
  };

  // ----- Submit -----
  const handleSubmit = async () => {
    if (isSubmitting) return;
    setIsSubmitting(true);
    const failed = [];

    let clientIp = '';
    try {
      const r = await fetch('https://api.ipify.org?format=json');
      const j = await r.json();
      clientIp = j.ip || '';
    } catch {}

    const userAgent = navigator.userAgent || '';

    try {
      for (const receipt of classifiedReceipts) {
        if (!receipt.file) {
          failed.push(receipt.fileName);
          continue;
        }
        try {
          const id = receipt.id;
          const filename = `${userData.name}_${id}`;
          const fileRef = ref(storage, `uploads/${filename}`);
          await uploadBytes(fileRef, receipt.file);
          const { file, classified, ...rest } = receipt;
          const data = {
            ...userData,
            ...rest,
            fileName: receipt.fileName,
            filePath: `uploads/${filename}`,
            status: 'pending',
            paid: false,
            submittedAt: serverTimestamp(),
            clientIp,
            userAgent,
          };
          await setDoc(doc(db, 'submissions', id), data);
        } catch {
          failed.push(receipt.fileName);
        }
      }

      if (failed.length === classifiedReceipts.length) {
        setFileError('Alle Belege konnten nicht hochgeladen werden. Bitte versuche es erneut.');
      } else {
        if (failed.length) setFileError(`Folgende Belege konnten nicht hochgeladen werden: ${failed.join(', ')}`);
        setOpenDialog(true);
        setSuccess(true);
        if (!savedDataExists) setUserData({ name: '', email: '', iban: '', bic: '' });
        setUploadedFiles([]);
        setClassifiedReceipts([]);
        setCurrentReceiptIndex(0);
        setActiveStep(0);
        setConfirmAmount(false);
        setConfirmUpload(false);
        setBicError(false);
      }
    } catch {
      setFileError('Ein Fehler ist aufgetreten. Bitte versuche es erneut.');
    } finally {
      setIsSubmitting(false);
    }
  };

  // ----- Validation -----
  const isReceiptComplete = (r) =>
    r.amount && isValidAmount(r.amount) && r.purpose && r.area && r.category && r.date && r.paymentType;

  const isUserDataValid = () =>
    userData.name.trim() &&
    userData.email &&
    isValidEmail(userData.email) &&
    userData.iban &&
    isValidIBAN(userData.iban) &&
    userData.bic;

  const isStepValid = (step) => {
    if (isMobile) {
      switch (step) {
        case 0: return isUserDataValid();
        case 1: return classifiedReceipts.length > 0 && classifiedReceipts.every(isReceiptComplete);
        case 2: return confirmAmount && confirmUpload;
        default: return false;
      }
    } else {
      switch (step) {
        case 0: return isUserDataValid();
        case 1: return uploadedFiles.length > 0;
        case 2: return classifiedReceipts.every(isReceiptComplete);
        case 3: return confirmAmount && confirmUpload;
        default: return false;
      }
    }
  };

  const canProceedToNext = () => isStepValid(activeStep);
  const canSubmit = () =>
    isMobile
      ? isStepValid(0) && isStepValid(1) && isStepValid(2)
      : isStepValid(0) && isStepValid(1) && isStepValid(2) && isStepValid(3);

  const progressPct = ((activeStep + 1) / steps.length) * 100;

  // -------------------------------------------------------------------------
  // Step renderers
  // -------------------------------------------------------------------------

  const renderUserDataStep = () => (
    <SectionCard>
      <SectionHeader
        icon={<Person />}
        eyebrow="Schritt 1"
        title="Kontaktdaten"
        subtitle="Wir brauchen deine Daten für die Rückerstattung."
      />
      <Stack spacing={2.5}>
        <Box>
          <FieldLabel required>Vollständiger Name</FieldLabel>
          <TextField
            name="name"
            value={userData.name}
            onChange={handleUserChange}
            fullWidth
            placeholder="Max Mustermann"
          />
        </Box>
        <Box>
          <FieldLabel required>E-Mail-Adresse</FieldLabel>
          <TextField
            name="email"
            type="email"
            value={userData.email}
            onChange={handleUserChange}
            fullWidth
            placeholder="max@example.com"
            error={userData.email.length > 0 && !isValidEmail(userData.email)}
            helperText={
              userData.email.length > 0 && !isValidEmail(userData.email)
                ? 'Bitte eine gültige E-Mail-Adresse eingeben.'
                : 'Für Rückfragen und Bestätigungen.'
            }
          />
        </Box>
        <Box>
          <FieldLabel required>IBAN</FieldLabel>
          <TextField
            name="iban"
            value={userData.iban}
            onChange={handleUserChange}
            fullWidth
            placeholder="DE89 3704 0044 0532 0130 00"
            error={userData.iban.length > 0 && !isValidIBAN(userData.iban)}
            helperText={
              userData.iban.length > 0 && !isValidIBAN(userData.iban)
                ? 'Die IBAN muss mit DE beginnen und 22 Zeichen haben.'
                : 'Für die Überweisung der Rückerstattung.'
            }
          />
        </Box>
        <Box>
          <FieldLabel required>BIC</FieldLabel>
          <TextField
            name="bic"
            value={userData.bic}
            fullWidth
            InputProps={{ readOnly: true }}
            placeholder="Wird automatisch ermittelt"
            helperText="Wird automatisch aus der IBAN ermittelt."
            sx={{ '& .MuiOutlinedInput-root': { backgroundColor: 'background.subtle' } }}
          />
        </Box>

        {bicError && userData.iban.trim().length > 0 && (
          <Alert
            severity="warning"
            action={
              <Button color="inherit" size="small" onClick={() => loadBIC(userData.iban)}>
                Erneut prüfen
              </Button>
            }
          >
            Keine gültige IBAN erkannt. BIC konnte nicht automatisch ermittelt werden.
          </Alert>
        )}

        {/* Save / load */}
        <Box
          sx={{
            mt: 1,
            p: 2.5,
            borderRadius: 3,
            backgroundColor: (t) => alpha(t.palette.primary.main, 0.04),
            border: (t) => `1px solid ${alpha(t.palette.primary.main, 0.12)}`,
          }}
        >
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 1 }}>
            <Save fontSize="small" color="primary" />
            <Typography variant="subtitle2" color="text.primary">
              Daten merken
            </Typography>
          </Box>
          <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
            Speichere deine Daten lokal, damit du sie beim nächsten Mal nicht erneut eingeben musst.
          </Typography>
          <Stack direction={{ xs: 'column', sm: 'row' }} spacing={1} sx={{ flexWrap: 'wrap' }}>
            <Button
              variant="contained"
              startIcon={<Save />}
              onClick={saveUserDataToLocalStorage}
              disabled={!userData.name || !userData.email || !userData.iban}
            >
              Speichern
            </Button>
            {savedDataExists && (
              <>
                <Button variant="outlined" startIcon={<Person />} onClick={loadUserDataFromLocalStorage}>
                  Laden
                </Button>
                <Button variant="outlined" color="error" startIcon={<RestoreFromTrash />} onClick={clearSavedUserData}>
                  Löschen
                </Button>
              </>
            )}
          </Stack>
          {savedDataExists && (
            <Alert severity="success" sx={{ mt: 2 }} icon={<CheckCircle fontSize="small" />}>
              Deine Daten sind gespeichert und werden automatisch geladen.
            </Alert>
          )}
        </Box>
      </Stack>
    </SectionCard>
  );

  const renderFileUploadStep = () => (
    <SectionCard>
      <SectionHeader
        icon={<CloudUpload />}
        eyebrow="Schritt 2"
        title="Belege hochladen"
        subtitle="Lade alle deine Belege gleichzeitig hoch — du klassifizierst sie im nächsten Schritt."
      />
      <Box
        sx={{
          p: 4,
          border: (t) => `2px dashed ${isDragOver ? t.palette.primary.main : t.palette.divider}`,
          borderRadius: 3,
          textAlign: 'center',
          cursor: 'pointer',
          backgroundColor: (t) => (isDragOver ? alpha(t.palette.primary.main, 0.06) : 'background.subtle'),
          transition: 'all 0.2s ease',
          '&:hover': {
            borderColor: 'primary.main',
            backgroundColor: (t) => alpha(t.palette.primary.main, 0.04),
          },
        }}
        onClick={() => document.getElementById('file-upload').click()}
        onDragOver={handleDragOver}
        onDragEnter={handleDragEnter}
        onDragLeave={handleDragLeave}
        onDrop={handleDrop}
      >
        <Box
          sx={{
            width: 56,
            height: 56,
            borderRadius: '50%',
            mx: 'auto',
            mb: 2,
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            backgroundColor: (t) => alpha(t.palette.primary.main, 0.12),
            color: 'primary.main',
          }}
        >
          <CloudUpload />
        </Box>
        <Typography variant="subtitle1" fontWeight={600} sx={{ mb: 0.5 }}>
          Dateien hier ablegen oder klicken
        </Typography>
        <Typography variant="body2" color="text.secondary">
          PDF, JPG, PNG · max. 10 MB pro Datei · mehrere Dateien möglich
        </Typography>
        <input
          id="file-upload"
          type="file"
          multiple
          accept=".pdf,image/*"
          onChange={handleFileUpload}
          style={{ display: 'none' }}
        />
      </Box>

      {fileError && (
        <Alert severity="error" sx={{ mt: 2 }} onClose={() => setFileError('')}>
          {fileError}
        </Alert>
      )}

      {uploadedFiles.length > 0 && (
        <Box sx={{ mt: 3 }}>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 2 }}>
            <Typography variant="subtitle1" fontWeight={600}>
              Hochgeladen
            </Typography>
            <Chip label={uploadedFiles.length} color="primary" size="small" />
          </Box>
          <Stack spacing={1.5}>
            {uploadedFiles.map((f) => (
              <Box
                key={f.id}
                sx={{
                  display: 'flex',
                  alignItems: 'center',
                  gap: 2,
                  p: 1.5,
                  pr: 1,
                  borderRadius: 2,
                  border: (t) => `1px solid ${t.palette.divider}`,
                  backgroundColor: 'background.paper',
                }}
              >
                <Box
                  sx={{
                    width: 48,
                    height: 48,
                    borderRadius: 1.5,
                    overflow: 'hidden',
                    flexShrink: 0,
                    bgcolor: 'background.subtle',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                  }}
                >
                  {f.type.startsWith('image/') ? (
                    <img
                      src={f.preview}
                      alt=""
                      style={{ width: '100%', height: '100%', objectFit: 'cover' }}
                    />
                  ) : f.type === 'application/pdf' ? (
                    <PictureAsPdf sx={{ color: 'error.main' }} />
                  ) : (
                    <AttachFile sx={{ color: 'text.secondary' }} />
                  )}
                </Box>
                <Box sx={{ flexGrow: 1, minWidth: 0 }}>
                  <Typography variant="body2" fontWeight={600} noWrap>
                    {f.name}
                  </Typography>
                  <Typography variant="caption" color="text.secondary">
                    {(f.size / 1024 / 1024).toFixed(2)} MB
                  </Typography>
                </Box>
                <IconButton onClick={() => removeFile(f.id)} size="small" color="error" aria-label="Entfernen">
                  <Delete fontSize="small" />
                </IconButton>
              </Box>
            ))}
          </Stack>
        </Box>
      )}
    </SectionCard>
  );

  const renderReceiptForm = (currentReceipt, onChange) => (
    <Stack spacing={2.5}>
      <Box>
        <FieldLabel required>Betrag</FieldLabel>
        <TextField
          fullWidth
          placeholder="z.B. 25,50"
          value={currentReceipt.amount}
          onChange={(e) => onChange('amount', e.target.value)}
          error={currentReceipt.amount !== '' && !isValidAmount(currentReceipt.amount)}
          helperText={
            currentReceipt.amount !== '' && !isValidAmount(currentReceipt.amount)
              ? 'Nur Zahlen erlaubt (z.B. 12,50 oder 12.50).'
              : ' '
          }
          InputProps={{ startAdornment: <Euro sx={{ mr: 1, color: 'text.secondary' }} /> }}
        />
      </Box>

      <Box>
        <FieldLabel required>Rechnungsdatum</FieldLabel>
        <TextField
          type="date"
          fullWidth
          value={currentReceipt.date}
          onChange={(e) => onChange('date', e.target.value)}
          InputLabelProps={{ shrink: true }}
          InputProps={{ startAdornment: <CalendarToday sx={{ mr: 1, color: 'text.secondary' }} /> }}
        />
      </Box>

      <Box>
        <FieldLabel required>Zweck / Beschreibung</FieldLabel>
        <TextField
          fullWidth
          multiline
          rows={3}
          value={currentReceipt.purpose}
          onChange={(e) => onChange('purpose', e.target.value)}
          placeholder="z.B. Einkauf für Grillstand – Fleisch und Gemüse"
          inputProps={{ maxLength: 140 }}
          helperText={`${(currentReceipt.purpose || '').length}/140`}
        />
      </Box>

      <Box>
        <FieldLabel required>Bereich</FieldLabel>
        <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1 }}>
          {areaOptions.map((opt) => {
            const selected = currentReceipt.area === opt;
            return (
              <Chip
                key={opt}
                label={opt}
                icon={<Storefront fontSize="small" />}
                onClick={() => {
                  onChange('area', opt);
                  savePreferredArea(opt);
                }}
                color={selected ? 'primary' : 'default'}
                variant={selected ? 'filled' : 'outlined'}
                sx={{ cursor: 'pointer', fontWeight: selected ? 600 : 500 }}
              />
            );
          })}
        </Box>
      </Box>

      <Box>
        <FieldLabel required>Kategorie</FieldLabel>
        <Autocomplete
          options={categoryOptions}
          value={currentReceipt.category}
          onChange={(_, v) => onChange('category', v || '')}
          renderInput={(params) => (
            <TextField
              {...params}
              placeholder="z.B. Verpflegung, Baumaterial …"
              InputProps={{
                ...params.InputProps,
                startAdornment: <Description sx={{ mr: 1, color: 'text.secondary' }} />,
              }}
            />
          )}
        />
      </Box>
    </Stack>
  );

  const renderMobileUploadAndClassifyStep = () => {
    const currentReceipt = uploadedFiles.length
      ? {
          id: uploadedFiles[0].id,
          file: uploadedFiles[0].file,
          fileName: uploadedFiles[0].name,
          amount: uploadedFiles[0].amount || '',
          purpose: uploadedFiles[0].purpose || '',
          area: uploadedFiles[0].area || '',
          category: uploadedFiles[0].category || '',
          date: uploadedFiles[0].date || new Date().toISOString().split('T')[0],
          paymentType: uploadedFiles[0].paymentType || 'paid',
          classified: false,
        }
      : null;

    const handleAddOrUpdate = () => {
      const newReceipt = {
        id: uploadedFiles[0].id,
        file: uploadedFiles[0].file,
        fileName: uploadedFiles[0].name,
        amount: uploadedFiles[0].amount || '',
        purpose: uploadedFiles[0].purpose || '',
        area: uploadedFiles[0].area || '',
        category: uploadedFiles[0].category || '',
        date: uploadedFiles[0].date || new Date().toISOString().split('T')[0],
        paymentType: uploadedFiles[0].paymentType || 'paid',
        classified: true,
      };
      if (editingReceiptId) {
        setClassifiedReceipts((prev) => prev.map((r) => (r.id === editingReceiptId ? newReceipt : r)));
        setEditingReceiptId(null);
      } else {
        setClassifiedReceipts((prev) => [...prev, newReceipt]);
      }
      setUploadedFiles([]);
    };

    return (
      <Stack spacing={2.5}>
        {uploadedFiles.length === 0 && (
          <SectionCard>
            <SectionHeader
              icon={<ReceiptLong />}
              eyebrow={`Beleg ${classifiedReceipts.length + 1}`}
              title="Beleg hochladen"
              subtitle="Foto aufnehmen oder Datei wählen."
            />
            <Box
              sx={{
                p: 4,
                border: (t) => `2px dashed ${isDragOver ? t.palette.primary.main : t.palette.divider}`,
                borderRadius: 3,
                textAlign: 'center',
                cursor: 'pointer',
                backgroundColor: (t) => (isDragOver ? alpha(t.palette.primary.main, 0.06) : 'background.subtle'),
                transition: 'all 0.2s ease',
              }}
              onClick={() => document.getElementById('mobile-file-upload').click()}
              onDragOver={handleDragOver}
              onDragEnter={handleDragEnter}
              onDragLeave={handleDragLeave}
              onDrop={handleDrop}
            >
              <Box
                sx={{
                  width: 56,
                  height: 56,
                  borderRadius: '50%',
                  mx: 'auto',
                  mb: 2,
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  backgroundColor: (t) => alpha(t.palette.primary.main, 0.12),
                  color: 'primary.main',
                }}
              >
                <CameraAlt />
              </Box>
              <Typography variant="subtitle1" fontWeight={600} sx={{ mb: 0.5 }}>
                Tippen zum Auswählen
              </Typography>
              <Typography variant="body2" color="text.secondary">
                Foto, PDF oder Bild · max. 10 MB
              </Typography>
              <input
                id="mobile-file-upload"
                type="file"
                accept=".pdf,image/*"
                capture="environment"
                onChange={handleFileUpload}
                style={{ display: 'none' }}
              />
            </Box>
            {fileError && (
              <Alert severity="error" sx={{ mt: 2 }} onClose={() => setFileError('')}>
                {fileError}
              </Alert>
            )}
          </SectionCard>
        )}

        {uploadedFiles.length > 0 && currentReceipt && (
          <SectionCard>
            <SectionHeader
              icon={<Edit />}
              eyebrow={editingReceiptId ? 'Bearbeiten' : `Beleg ${classifiedReceipts.length + 1}`}
              title="Belegdaten"
            />
            <Box sx={{ mb: 2.5 }}>
              <FilePreview file={currentReceipt.file} name={currentReceipt.fileName} type={currentReceipt.file.type} height={180} />
            </Box>
            {renderReceiptForm(currentReceipt, (field, value) => {
              setUploadedFiles([{ ...uploadedFiles[0], [field]: value }]);
            })}
            <Stack spacing={1} sx={{ mt: 3 }}>
              <Button
                variant="contained"
                size="large"
                fullWidth
                startIcon={<Add />}
                onClick={handleAddOrUpdate}
                disabled={
                  !currentReceipt.amount ||
                  !isValidAmount(currentReceipt.amount) ||
                  !currentReceipt.purpose ||
                  !currentReceipt.area ||
                  !currentReceipt.category ||
                  !currentReceipt.date ||
                  !currentReceipt.paymentType
                }
                sx={{ bgcolor: 'success.main', '&:hover': { bgcolor: 'success.dark' } }}
              >
                {editingReceiptId ? 'Beleg aktualisieren' : 'Beleg hinzufügen'}
              </Button>
              <Button
                variant="outlined"
                size="large"
                fullWidth
                onClick={() => (editingReceiptId ? cancelEdit() : setUploadedFiles([]))}
              >
                {editingReceiptId ? 'Abbrechen' : 'Anderen Beleg wählen'}
              </Button>
            </Stack>
          </SectionCard>
        )}

        {classifiedReceipts.length > 0 && (
          <SectionCard>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 2 }}>
              <Typography variant="subtitle1" fontWeight={700}>
                Erfasste Belege
              </Typography>
              <Chip label={classifiedReceipts.length} color="success" size="small" />
            </Box>
            <Stack spacing={1.5}>
              {classifiedReceipts.map((r) => (
                <ReceiptListItem
                  key={r.id}
                  receipt={r}
                  onEdit={() => editReceipt(r.id)}
                  onDelete={() => deleteReceipt(r.id)}
                />
              ))}
            </Stack>
          </SectionCard>
        )}
      </Stack>
    );
  };

  const renderClassificationStep = () => {
    const currentReceipt = classifiedReceipts[currentReceiptIndex];
    if (!currentReceipt) return null;

    return (
      <Stack spacing={3}>
        <SectionCard>
          <SectionHeader
            icon={<Edit />}
            eyebrow={`${currentReceiptIndex + 1} von ${classifiedReceipts.length}`}
            title="Belege klassifizieren"
            subtitle="Trage zu jedem Beleg die Daten ein."
          />
          <Stack direction="row" spacing={1} sx={{ flexWrap: 'wrap', gap: 1 }}>
            {classifiedReceipts.map((r, i) => (
              <Chip
                key={r.id}
                icon={r.classified ? <CheckCircle /> : undefined}
                label={`${i + 1}`}
                onClick={() => setCurrentReceiptIndex(i)}
                color={r.classified ? 'success' : i === currentReceiptIndex ? 'primary' : 'default'}
                variant={i === currentReceiptIndex ? 'filled' : 'outlined'}
                sx={{ cursor: 'pointer', minWidth: 36 }}
              />
            ))}
          </Stack>
        </SectionCard>

        <Box sx={{ display: 'grid', gap: 3, gridTemplateColumns: { xs: '1fr', md: '5fr 7fr' } }}>
          <SectionCard>
            <Typography variant="subtitle2" color="text.secondary" sx={{ mb: 1 }}>
              Belegvorschau
            </Typography>
            <Typography variant="body2" fontWeight={600} sx={{ mb: 2 }} noWrap>
              {currentReceipt.fileName}
            </Typography>
            <FilePreview file={currentReceipt.file} name={currentReceipt.fileName} type={currentReceipt.file.type} height={300} />
            {currentReceipt.file.type === 'application/pdf' && (
              <Button
                size="small"
                startIcon={<OpenInNew />}
                onClick={() =>
                  window.open(currentReceipt.file.preview || URL.createObjectURL(currentReceipt.file), '_blank')
                }
                sx={{ mt: 1 }}
              >
                In neuem Tab öffnen
              </Button>
            )}
          </SectionCard>

          <SectionCard>
            <Typography variant="subtitle2" color="text.secondary" sx={{ mb: 2 }}>
              Belegdaten
            </Typography>
            {renderReceiptForm(currentReceipt, updateReceiptClassification)}
            <Box sx={{ display: 'flex', justifyContent: 'space-between', mt: 3, gap: 2 }}>
              <Button
                startIcon={<ArrowBack />}
                onClick={handlePrevReceipt}
                disabled={currentReceiptIndex === 0}
                variant="outlined"
              >
                Vorheriger
              </Button>
              <Button
                variant="contained"
                onClick={() => {
                  markReceiptAsClassified();
                  if (currentReceiptIndex < classifiedReceipts.length - 1) handleNextReceipt();
                }}
                disabled={
                  !currentReceipt.amount ||
                  !isValidAmount(currentReceipt.amount) ||
                  !currentReceipt.purpose ||
                  !currentReceipt.area ||
                  !currentReceipt.category ||
                  !currentReceipt.date
                }
                endIcon={<ArrowForward />}
              >
                {currentReceiptIndex < classifiedReceipts.length - 1 ? 'Nächster' : 'Fertig'}
              </Button>
            </Box>
          </SectionCard>
        </Box>
      </Stack>
    );
  };

  const renderOverviewStep = () => {
    const total = classifiedReceipts.reduce((sum, r) => sum + parseFloat(r.amount || 0), 0);
    return (
      <Stack spacing={3}>
        <SectionCard>
          <SectionHeader icon={<Insights />} eyebrow="Letzter Schritt" title="Zusammenfassung" subtitle="Prüfe deine Angaben und sende die Belege ab." />
          <Stack direction={{ xs: 'column', sm: 'row' }} spacing={2}>
            <KPI label="Belege" value={classifiedReceipts.length} />
            <KPI label="Gesamtbetrag" value={`${total.toFixed(2).replace('.', ',')} €`} accent />
          </Stack>
        </SectionCard>

        <SectionCard>
          <Typography variant="subtitle2" color="text.secondary" sx={{ mb: 1.5 }}>
            Kontaktdaten
          </Typography>
          <Stack spacing={0.75}>
            <SummaryRow label="Name" value={userData.name} />
            <SummaryRow label="E-Mail" value={userData.email} />
            <SummaryRow label="IBAN" value={userData.iban} mono />
            <SummaryRow label="BIC" value={userData.bic} mono />
          </Stack>
        </SectionCard>

        <SectionCard>
          <Typography variant="subtitle2" color="text.secondary" sx={{ mb: 1.5 }}>
            Belege ({classifiedReceipts.length})
          </Typography>
          <Stack spacing={1.5}>
            {classifiedReceipts.map((r, i) => (
              <Box
                key={r.id}
                sx={{
                  p: 2,
                  borderRadius: 2,
                  border: (t) => `1px solid ${t.palette.divider}`,
                  backgroundColor: 'background.paper',
                }}
              >
                <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', gap: 1, mb: 1 }}>
                  <Typography variant="body2" fontWeight={600}>
                    Beleg {i + 1} · {r.fileName}
                  </Typography>
                  <Typography variant="body1" fontWeight={700} color="primary.main">
                    {r.amount} €
                  </Typography>
                </Box>
                <Typography variant="body2" color="text.secondary" sx={{ mb: 1 }}>
                  {r.purpose}
                </Typography>
                <Stack direction="row" spacing={0.75} sx={{ flexWrap: 'wrap', gap: 0.75 }}>
                  <Chip size="small" label={r.area} />
                  <Chip size="small" label={r.category} variant="outlined" />
                  <Chip size="small" label={r.date} variant="outlined" />
                </Stack>
              </Box>
            ))}
          </Stack>
        </SectionCard>

        <SectionCard>
          <Stack spacing={1}>
            <FormControlLabel
              control={<Checkbox checked={confirmAmount} onChange={(e) => setConfirmAmount(e.target.checked)} />}
              label={<Typography variant="body2">Ich bestätige, dass alle Beträge mit den Belegen übereinstimmen und lesbar hochgeladen wurden.</Typography>}
            />
            <FormControlLabel
              control={<Checkbox checked={confirmUpload} onChange={(e) => setConfirmUpload(e.target.checked)} />}
              label={<Typography variant="body2">Ich akzeptiere die Speicherung meiner Daten zur Bearbeitung meines Antrags.</Typography>}
            />
          </Stack>
        </SectionCard>
      </Stack>
    );
  };

  // -------------------------------------------------------------------------
  // Layout
  // -------------------------------------------------------------------------

  const stepContent = () => {
    if (activeStep === 0) return renderUserDataStep();
    if (isMobile) {
      if (activeStep === 1) return renderMobileUploadAndClassifyStep();
      if (activeStep === 2) return renderOverviewStep();
    } else {
      if (activeStep === 1) return renderFileUploadStep();
      if (activeStep === 2) return renderClassificationStep();
      if (activeStep === 3) return renderOverviewStep();
    }
    return null;
  };

  return (
    <Box sx={{ minHeight: '100vh', bgcolor: 'background.default', pb: { xs: 12, md: 4 } }}>
      {/* Mobile App Bar with progress */}
      {isMobile && (
        <AppBar
          position="sticky"
          elevation={0}
          sx={{
            bgcolor: 'background.paper',
            color: 'text.primary',
            borderBottom: (t) => `1px solid ${t.palette.divider}`,
            top: 0,
          }}
        >
          <Toolbar sx={{ minHeight: 56, px: 2 }}>
            <Box
              sx={{
                width: 36,
                height: 36,
                borderRadius: 1.5,
                bgcolor: 'primary.main',
                color: 'primary.contrastText',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                mr: 1.5,
              }}
            >
              <ReceiptLong fontSize="small" />
            </Box>
            <Box sx={{ flexGrow: 1 }}>
              <Typography variant="subtitle2" fontWeight={700} sx={{ lineHeight: 1.1 }}>
                Belege einreichen
              </Typography>
              <Typography variant="caption" color="text.secondary">
                Schritt {activeStep + 1} von {steps.length} · {steps[activeStep]}
              </Typography>
            </Box>
            <Typography variant="caption" color="text.secondary" fontWeight={600}>
              {Math.round(progressPct)}%
            </Typography>
          </Toolbar>
          <LinearProgress variant="determinate" value={progressPct} />
        </AppBar>
      )}

      <Container maxWidth="lg" sx={{ pt: { xs: 2, md: 4 } }}>
        <Stack spacing={{ xs: 2, md: 3 }}>
          {/* Desktop hero */}
          {!isMobile && (
            <SectionCard>
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
                <Box
                  sx={{
                    width: 56,
                    height: 56,
                    borderRadius: 2,
                    bgcolor: 'primary.main',
                    color: 'primary.contrastText',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                  }}
                >
                  <ReceiptLong />
                </Box>
                <Box>
                  <Typography variant="h4" fontWeight={700}>
                    Belege einreichen
                  </Typography>
                  <Typography variant="body1" color="text.secondary">
                    Reiche alle Auslagen ein, die dir im Rahmen des Festivals entstanden sind.
                  </Typography>
                </Box>
              </Box>
              <Box sx={{ mt: 3 }}>
                <OriginalsBanner />
              </Box>
              <Alert severity="info" icon={<AccountBalance fontSize="small" />} sx={{ mt: 2 }}>
                Alle Rückerstattungen erfolgen ausschließlich per Überweisung. Bitte deine Bankdaten korrekt eingeben.
              </Alert>
            </SectionCard>
          )}

          {/* Mobile: Originals banner directly under app bar */}
          {isMobile && (
            <OriginalsBanner compact />
          )}

          {/* Stepper */}
          {!isMobile && (
            <SectionCard>
              <Stepper activeStep={activeStep} alternativeLabel>
                {steps.map((label) => (
                  <Step key={label}>
                    <StepLabel>{label}</StepLabel>
                  </Step>
                ))}
              </Stepper>
            </SectionCard>
          )}

          <Box>{stepContent()}</Box>

          {/* Desktop nav inline */}
          {!isMobile && (
            <SectionCard sx={{ p: 2 }}>
              <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                <Button
                  disabled={activeStep === 0}
                  onClick={handleBack}
                  startIcon={<ArrowBack />}
                  size="large"
                  variant="outlined"
                >
                  Zurück
                </Button>
                {activeStep === steps.length - 1 ? (
                  <Button
                    variant="contained"
                    onClick={handleSubmit}
                    disabled={!canSubmit() || isSubmitting}
                    size="large"
                    color="success"
                    startIcon={isSubmitting ? <CircularProgress size={18} color="inherit" /> : <CheckCircle />}
                  >
                    {isSubmitting ? 'Wird eingereicht …' : 'Belege einreichen'}
                  </Button>
                ) : (
                  <Button
                    variant="contained"
                    onClick={handleNext}
                    disabled={!canProceedToNext()}
                    endIcon={<ArrowForward />}
                    size="large"
                  >
                    Weiter
                  </Button>
                )}
              </Box>
            </SectionCard>
          )}

          {fileError && !isMobile && (
            <Alert severity="error" onClose={() => setFileError('')}>
              {fileError}
            </Alert>
          )}

          {success && !isMobile && (
            <Fade in>
              <Alert severity="success">Belege wurden erfolgreich eingereicht!</Alert>
            </Fade>
          )}
        </Stack>
      </Container>

      {/* Mobile sticky bottom action bar */}
      {isMobile && (
        <Box
          sx={{
            position: 'fixed',
            left: 0,
            right: 0,
            bottom: 0,
            zIndex: 1100,
            bgcolor: 'background.paper',
            borderTop: (t) => `1px solid ${t.palette.divider}`,
            px: 2,
            pt: 1.5,
            pb: `calc(12px + env(safe-area-inset-bottom, 0px))`,
            backdropFilter: 'blur(8px)',
          }}
        >
          {fileError && (
            <Alert severity="error" sx={{ mb: 1 }} onClose={() => setFileError('')}>
              {fileError}
            </Alert>
          )}
          <Stack direction="row" spacing={1.5}>
            <Button
              disabled={activeStep === 0}
              onClick={handleBack}
              variant="outlined"
              size="large"
              sx={{ flex: '0 0 auto', minWidth: 110 }}
              startIcon={<ArrowBack />}
            >
              Zurück
            </Button>
            {activeStep === steps.length - 1 ? (
              <Button
                variant="contained"
                color="success"
                onClick={handleSubmit}
                disabled={!canSubmit() || isSubmitting}
                size="large"
                fullWidth
                startIcon={isSubmitting ? <CircularProgress size={18} color="inherit" /> : <CheckCircle />}
              >
                {isSubmitting ? 'Sende …' : 'Einreichen'}
              </Button>
            ) : (
              <Button
                variant="contained"
                onClick={handleNext}
                disabled={!canProceedToNext()}
                size="large"
                fullWidth
                endIcon={<ArrowForward />}
              >
                Weiter
              </Button>
            )}
          </Stack>
        </Box>
      )}

      {/* Success Dialog */}
      <Dialog
        open={openDialog}
        onClose={() => setOpenDialog(false)}
        fullWidth
        maxWidth="sm"
        fullScreen={isMobile}
        scroll="paper"
        PaperProps={{ sx: { borderRadius: isMobile ? 0 : 5 } }}
      >
        <DialogTitle sx={{ textAlign: 'center', pt: { xs: 4, sm: 5 }, pb: 1 }}>
          <Box
            sx={{
              width: 72,
              height: 72,
              borderRadius: '50%',
              bgcolor: (t) => alpha(t.palette.success.main, 0.12),
              color: 'success.main',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              mx: 'auto',
              mb: 2,
            }}
          >
            <CheckCircle sx={{ fontSize: 40 }} />
          </Box>
          <Typography variant="h4" fontWeight={700}>
            Vielen Dank!
          </Typography>
          <Typography variant="body2" color="text.secondary" sx={{ mt: 1 }}>
            Deine Belege wurden erfolgreich eingereicht.
          </Typography>
        </DialogTitle>
        <DialogContent sx={{ pt: 2 }}>
          <Stack spacing={2.5}>
            {/* Originals warning - prominent */}
            <OriginalsBanner />

            {/* Process steps */}
            <Box>
              <Typography variant="overline" color="text.secondary" sx={{ fontWeight: 700, letterSpacing: 1 }}>
                Wie es weitergeht
              </Typography>
              <Stack spacing={1.5} sx={{ mt: 1 }}>
                <ProcessStep
                  number="1"
                  title="Prüfung"
                  body="Wir prüfen jeden Beleg auf Vollständigkeit und Plausibilität."
                />
                <ProcessStep
                  number="2"
                  title="Rückfragen (falls nötig)"
                  body="Bei Unklarheiten melden wir uns per E-Mail. Bitte schau gelegentlich in dein Postfach – auch in den Spam-Ordner."
                />
                <ProcessStep
                  number="3"
                  title="Überweisung"
                  body="Nach erfolgreicher Prüfung überweisen wir den Betrag innerhalb der nächsten Tage auf das von dir angegebene Konto."
                />
              </Stack>
            </Box>

            {/* Quick reminders */}
            <Box
              sx={{
                p: 2,
                borderRadius: 2,
                bgcolor: 'background.subtle',
                border: (t) => `1px solid ${t.palette.divider}`,
              }}
            >
              <Typography variant="subtitle2" fontWeight={700} sx={{ mb: 1 }}>
                Gut zu wissen
              </Typography>
              <Stack spacing={0.75} sx={{ '& > *': { display: 'flex', gap: 1, alignItems: 'flex-start' } }}>
                <Box>
                  <CheckCircle sx={{ color: 'success.main', fontSize: 18, mt: '2px' }} />
                  <Typography variant="body2" color="text.primary">
                    Du erhältst keine separate E-Mail-Bestätigung – diese Anzeige ist die Bestätigung.
                  </Typography>
                </Box>
                <Box>
                  <CheckCircle sx={{ color: 'success.main', fontSize: 18, mt: '2px' }} />
                  <Typography variant="body2" color="text.primary">
                    Die Bearbeitung dauert in der Regel wenige Tage.
                  </Typography>
                </Box>
                <Box>
                  <CheckCircle sx={{ color: 'success.main', fontSize: 18, mt: '2px' }} />
                  <Typography variant="body2" color="text.primary">
                    Bei Fragen wende dich an die Festival-Orga.
                  </Typography>
                </Box>
              </Stack>
            </Box>
          </Stack>
        </DialogContent>
        <DialogActions sx={{ justifyContent: 'center', px: 3, pb: { xs: 3, sm: 4 } }}>
          <Button
            onClick={() => setOpenDialog(false)}
            variant="contained"
            size="large"
            fullWidth={isMobile}
            sx={{ px: 5 }}
          >
            Verstanden
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
}

// ---------------------------------------------------------------------------
// Sub-components
// ---------------------------------------------------------------------------

const ReceiptListItem = ({ receipt, onEdit, onDelete }) => (
  <Box
    sx={{
      p: 2,
      borderRadius: 2,
      border: (t) => `1px solid ${t.palette.divider}`,
      backgroundColor: 'background.paper',
    }}
  >
    <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', gap: 1, mb: 1 }}>
      <Box sx={{ minWidth: 0, flex: 1 }}>
        <Typography variant="body2" fontWeight={600} noWrap>
          {receipt.fileName}
        </Typography>
        <Typography variant="caption" color="text.secondary" sx={{ display: 'block' }}>
          {receipt.area} · {receipt.category}
        </Typography>
      </Box>
      <Typography variant="body1" fontWeight={700} color="primary.main">
        {receipt.amount} €
      </Typography>
    </Box>
    <Box sx={{ display: 'flex', gap: 1, alignItems: 'center', flexWrap: 'wrap' }}>
      <Box sx={{ flexGrow: 1 }} />
      <Button size="small" variant="text" onClick={onEdit} startIcon={<Edit fontSize="small" />}>
        Bearbeiten
      </Button>
      <Button size="small" variant="text" color="error" onClick={onDelete} startIcon={<Delete fontSize="small" />}>
        Löschen
      </Button>
    </Box>
  </Box>
);

const KPI = ({ label, value, accent }) => (
  <Box
    sx={{
      flex: 1,
      p: 2,
      borderRadius: 2,
      border: (t) => `1px solid ${t.palette.divider}`,
      backgroundColor: accent ? (t) => alpha(t.palette.primary.main, 0.06) : 'background.subtle',
    }}
  >
    <Typography variant="caption" color="text.secondary" sx={{ textTransform: 'uppercase', letterSpacing: 0.6 }}>
      {label}
    </Typography>
    <Typography variant="h5" fontWeight={700} color={accent ? 'primary.main' : 'text.primary'}>
      {value}
    </Typography>
  </Box>
);

const SummaryRow = ({ label, value, mono }) => (
  <Box sx={{ display: 'flex', gap: 2, alignItems: 'baseline' }}>
    <Typography variant="caption" color="text.secondary" sx={{ width: 80, flexShrink: 0 }}>
      {label}
    </Typography>
    <Typography
      variant="body2"
      sx={{ fontWeight: 500, fontFamily: mono ? 'ui-monospace, SFMono-Regular, monospace' : undefined, wordBreak: 'break-all' }}
    >
      {value}
    </Typography>
  </Box>
);

const ProcessStep = ({ number, title, body }) => (
  <Box sx={{ display: 'flex', gap: 1.5, alignItems: 'flex-start' }}>
    <Box
      sx={{
        flexShrink: 0,
        width: 28,
        height: 28,
        borderRadius: '50%',
        bgcolor: 'primary.main',
        color: 'primary.contrastText',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        fontSize: 13,
        fontWeight: 700,
        mt: '2px',
      }}
    >
      {number}
    </Box>
    <Box sx={{ flex: 1 }}>
      <Typography variant="subtitle2" fontWeight={700} color="text.primary">
        {title}
      </Typography>
      <Typography variant="body2" color="text.secondary">
        {body}
      </Typography>
    </Box>
  </Box>
);

export default SubmissionForm;
